import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import LockedFeature from "@/components/LockedFeature";
import { hasPlan } from "@/lib/plans";
import { api } from "@/lib/api";
import { GAMLY_PERSONALITIES, LANGUAGES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { captureError } from "@/lib/sentry";
import PredictPanel from "@/components/PredictPanel";
import AskGamlyOverlay from "@/components/AskGamlyOverlay";
import PlayByPlay from "@/components/PlayByPlay";
import TeamLogo from "@/components/TeamLogo";
import {
  ArrowLeftIcon, TelevisionIcon, ArrowsClockwiseIcon, GearSixIcon,
} from "@phosphor-icons/react";
import WorldSeriesBadge from "@/components/WorldSeriesBadge";

const TAG_COLORS = {
  "BIG PLAY": "#FF453A",
  "KEY STAT": "#0A84FF",
  "STORYLINE": "#FDB927",
  "HOT TAKE": "#FF9F0A",
  "WATCH NEXT": "#32D74B",
};

function tagColor(tag) {
  return TAG_COLORS[tag] || "#0A84FF";
}

function Scoreboard({ game }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-md overflow-hidden mb-5" data-testid="watch-scoreboard">
      <div className="px-5 py-3 flex items-center justify-between gap-2 border-b border-white/5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[11px] font-black uppercase tracking-[0.2em] text-white/40">{game.league}</span>
          <WorldSeriesBadge game={game} />
          {game.tv && (
            <span className="hidden md:inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/40">
              <TelevisionIcon size={12} /> {game.tv}
            </span>
          )}
        </div>
        {game.status === "LIVE" ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#FF0000]/15 border border-[#FF453A]/40">
            <span className="live-dot" />
            <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[#FF453A]">{game.period_label}</span>
          </span>
        ) : (
          <span className="text-[11px] font-black uppercase tracking-[0.15em] text-white/60">{game.period_label}</span>
        )}
      </div>
      <div className="px-5 py-4 grid grid-cols-2 gap-4">
        {[
          { abbr: game.away_team_abbr, name: game.away_team, score: game.away_score, color: game.away_team_color, logo: game.away_team_logo, home: false },
          { abbr: game.home_team_abbr, name: game.home_team, score: game.home_score, color: game.home_team_color, logo: game.home_team_logo, home: true },
        ].map((t) => (
          <div key={t.abbr} className={`flex flex-col gap-1 ${t.home ? "items-end text-right" : "items-start"}`}>
            <div className={`flex items-center gap-2 ${t.home ? "flex-row-reverse" : ""}`}>
              <TeamLogo abbr={t.abbr} logo={t.logo} color={t.color} size={40} />
              <p className="font-display font-bold uppercase text-sm tracking-tight text-white/85 max-w-[140px] truncate">{t.name}</p>
            </div>
            <div className="font-num text-5xl font-black">{t.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WatchGame() {
  const { user } = useAuth();
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [status, setStatus] = useState("loading");
  const [cards, setCards] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const langRef = useRef(language);
  langRef.current = language;

  const canFeed = hasPlan(user, "pro");
  const persona = GAMLY_PERSONALITIES.find((p) => p.value === (user?.personality || "broadcaster"));

  const fetchGame = useCallback(async () => {
    try {
      const r = await api.get(`/games/${id}`);
      setGame(r.data);
      setStatus("ready");
      return r.data;
    } catch (err) {
      setStatus(err?.response?.status === 404 ? "not_found" : "error");
      if (err?.response?.status !== 404) captureError("watch game load failed:", err);
      return null;
    }
  }, [id]);

  const fetchFeed = useCallback(async () => {
    if (!canFeed) return;
    setFeedLoading(true);
    try {
      const r = await api.post(`/watch/${id}/feed`, { language: langRef.current });
      setCards((prev) => {
        const merged = [...r.data.cards, ...prev];
        return merged.slice(0, 24);
      });
    } catch (err) {
      captureError("watch feed failed:", err);
    } finally {
      setFeedLoading(false);
    }
  }, [id, canFeed]);

  // Initial load
  useEffect(() => {
    setStatus("loading");
    setCards([]);
    (async () => {
      const g = await fetchGame();
      if (g) fetchFeed();
    })();
  }, [fetchGame, fetchFeed]);

  // Poll every 30s for LIVE games (refresh score + generate fresh feed cards)
  useEffect(() => {
    if (!game || game.status !== "LIVE") return undefined;
    const t = setInterval(() => {
      fetchGame();
      fetchFeed();
    }, 30000);
    return () => clearInterval(t);
  }, [game, fetchGame, fetchFeed]);

  if (status === "loading") {
    return <AppLayout><div className="text-white/60" data-testid="watch-game-loading">Tuning in…</div></AppLayout>;
  }
  if (status === "not_found") {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="watch-not-found">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF453A] mb-2">404 · Game Not Found</p>
          <h1 className="font-display font-bold text-4xl uppercase tracking-tighter mb-3">This game isn't on today's slate</h1>
          <Link to="/watch" className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider">
            <ArrowLeftIcon /> Back to Watch
          </Link>
        </div>
      </AppLayout>
    );
  }
  if (status === "error" || !game) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="watch-error">
          <h1 className="font-display font-bold text-3xl uppercase tracking-tighter mb-3">Couldn't load this game</h1>
          <Link to="/watch" className="inline-flex items-center gap-1 px-5 py-2 rounded-md border border-white/20 text-white/80 text-xs font-bold uppercase tracking-wider hover:bg-white/5">
            <ArrowLeftIcon /> Back to Watch
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4">
        <Link to="/watch" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white" data-testid="watch-back">
          <ArrowLeftIcon size={14} /> Back to Watch
        </Link>
        <Link to="/settings" data-testid="watch-game-persona-chip" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#121212] border border-white/10 hover:border-white/25 transition-colors">
          {persona && <persona.icon weight="bold" size={14} className="text-[#0A84FF]" />}
          <span className="text-[11px] font-bold uppercase tracking-wider text-white/80">{persona?.label}</span>
          <GearSixIcon size={12} className="text-white/40" />
        </Link>
      </div>

      <Scoreboard game={game} />

      <PredictPanel gameId={id} gameStatus={game.status} />

      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display font-bold text-2xl uppercase tracking-tighter">Live Feed</h2>
          <p className="text-xs text-white/45">{game.status === "LIVE" ? "Auto-refreshing every 30s" : "Tap refresh for fresh takes"}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="watch-lang-select"
            className="bg-black border border-white/15 text-white/80 text-xs uppercase font-bold tracking-wider rounded-md px-2.5 py-2">
            {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
          <button onClick={fetchFeed} disabled={feedLoading || !canFeed} data-testid="watch-refresh-feed"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#0A84FF]/50 text-[#0A84FF] hover:bg-[#0A84FF]/10 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            <ArrowsClockwiseIcon weight="bold" size={14} className={feedLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {!canFeed && (
        <LockedFeature
          testId="watch-feed-locked"
          title="Live Game Companion"
          desc="Get Gamly's live insight feed for every game. Free fans can still ask 5 questions a day below."
        />
      )}

      {feedLoading && cards.length === 0 && (
        <div className="p-4 rounded-md bg-[#121212] border border-white/10 text-sm text-white/70" data-testid="watch-feed-loading">
          Gamly is breaking down the action…
        </div>
      )}

      <div className="space-y-3" data-testid="watch-feed">
        {cards.map((c) => (
          <div key={c.id} data-testid={`watch-card-${c.id}`}
            className="p-4 rounded-md bg-[#121212] border border-white/10 fade-up">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-black uppercase tracking-[0.15em] px-2 py-0.5 rounded"
                style={{ backgroundColor: `${tagColor(c.tag)}1f`, color: tagColor(c.tag), border: `1px solid ${tagColor(c.tag)}55` }}>
                {c.tag}
              </span>
              <span className="font-display font-bold text-base uppercase tracking-tight">{c.title}</span>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">{c.insight}</p>
          </div>
        ))}
        {canFeed && !feedLoading && cards.length === 0 && (
          <div className="text-white/50 text-sm" data-testid="watch-feed-empty">No insights yet — tap refresh to get Gamly's read.</div>
        )}
      </div>

      <div className="mt-6">
        <PlayByPlay gameId={id} gameStatus={game.status} />
      </div>

      <AskGamlyOverlay gameId={id} language={language} persona={persona} />
    </AppLayout>
  );
}
